import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Box, Text, Badge, Flex, Select, Input,
  useToast, FormControl, FormLabel, Table, Thead, Tbody, Tr, Th, Td, useDisclosure,
} from '@chakra-ui/react';
import { FiCalendar, FiPlus, FiCheck, FiX, FiSettings } from 'react-icons/fi';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import NationalHolidaysModal from './NationalHolidaysModal';

const STATUS_COLORS = { requested:'orange', approved:'green', rejected:'red', withdrawn:'gray', cancelled:'gray' };
const TYPE_LABELS = { annual:'Annual Leave', unpaid:'Unpaid', compassionate:'Compassionate', maternity:'Maternity', paternity:'Paternity', other:'Other' };

export default function HolidayDashboard({ isOpen, onClose }) {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterUser, setFilterUser] = useState('');
  const [viewRange, setViewRange] = useState('year');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ start_date: '', end_date: '', days_count: '', request_type: 'annual', notes: '' });
  const [saving, setSaving] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [adminData, setAdminData] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [showPeriodForm, setShowPeriodForm] = useState(false);
  const [periodForm, setPeriodForm] = useState({ name: '', start_date: '', end_date: '', scope: 'standard', department_id: '', user_id: '' });
  const [departments, setDepartments] = useState([]);
  const adminDisclosure = useDisclosure();
  const nationalHolidaysDisclosure = useDisclosure();
  const toast = useToast();
  const isAdmin = user?.role === 'admin';

  const fetchSummary = useCallback(async (uid) => {
    try { const { data } = await api.get(`/holidays/summary/${uid || user?.id}`); setSummary(data); } catch(e) {}
  }, [user]);

  const fetchRequests = useCallback(async () => {
    try {
      const today = new Date();
      let from, to;
      if (viewRange === 'year') { from = new Date(today.getFullYear(), 0, 1); to = new Date(today.getFullYear(), 11, 31); }
      else if (viewRange === '3month') { from = today; to = new Date(today.getTime() + 90 * 86400000); }
      else if (viewRange === 'month') { from = new Date(today.getFullYear(), today.getMonth(), 1); to = new Date(today.getFullYear(), today.getMonth() + 1, 0); }
      else { from = today; to = new Date(today.getTime() + 14 * 86400000); }
      const params = new URLSearchParams();
      if (filterUser) params.set('user_id', filterUser); else params.set('user_id', user?.id);
      params.set('from', from.toISOString().split('T')[0]);
      params.set('to', to.toISOString().split('T')[0]);
      const { data } = await api.get(`/holidays/requests?${params.toString()}`);
      setRequests(data);
    } catch(e) {}
  }, [filterUser, viewRange, user]);

  const fetchUsers = async () => { try { const { data } = await api.get('/calendar/users'); setUsers(data); } catch(e) {} };
  const fetchAdmin = async () => { try { const { data } = await api.get('/holidays/admin/overview'); setAdminData(data); } catch(e) {} };
  const fetchPeriods = async () => { try { const { data } = await api.get('/holidays/periods'); setPeriods(data); } catch(e) {} };
  const fetchDepartments = async () => { try { const { data } = await api.get('/departments'); setDepartments(data); } catch(e) {} };

  useEffect(() => { if (isOpen) { fetchSummary(); fetchRequests(); fetchUsers(); } }, [isOpen, fetchSummary, fetchRequests]);

  const handleSubmit = async () => {
    if (!form.start_date || !form.end_date) { toast({ title: 'Dates required', status: 'warning', duration: 2000 }); return; }
    setSaving(true);
    try {
      await api.post('/holidays/requests', form);
      await fetchRequests(); await fetchSummary();
      setShowForm(false); setForm({ start_date: '', end_date: '', days_count: '', request_type: 'annual', notes: '' });
      toast({ title: 'Holiday requested', status: 'success', duration: 1500 });
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    finally { setSaving(false); }
  };

  const handleAction = async (id, action, reason) => {
    try {
      if (action === 'approve') await api.put(`/holidays/requests/${id}/approve`);
      else if (action === 'reject') await api.put(`/holidays/requests/${id}/reject`, { reason });
      else if (action === 'withdraw') await api.put(`/holidays/requests/${id}/withdraw`);
      await fetchRequests(); await fetchSummary();
      toast({ title: action.charAt(0).toUpperCase() + action.slice(1) + 'd', status: 'success', duration: 1500 });
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
  };

  const handleSavePeriod = async () => {
    try {
      await api.post('/holidays/periods', periodForm);
      await fetchPeriods();
      setShowPeriodForm(false); setPeriodForm({ name: '', start_date: '', end_date: '', scope: 'standard', department_id: '', user_id: '' });
      toast({ title: 'Period saved', status: 'success', duration: 1500 });
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
  };

  // Auto-calc days
  useEffect(() => {
    if (form.start_date && form.end_date) {
      const s = new Date(form.start_date), e = new Date(form.end_date);
      let days = 0;
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        if (d.getDay() !== 0 && d.getDay() !== 6) days++;
      }
      setForm(p => ({ ...p, days_count: days }));
    }
  }, [form.start_date, form.end_date]);

  const fmtDate = (d) => d ? new Date(d.split ? d.split('T')[0] + 'T12:00:00' : d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  return (
    <>
    <Modal isOpen={isOpen && !showAdmin} onClose={onClose} size="5xl" scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="90vh">
        <ModalHeader fontSize="md">
          <Flex justify="space-between" align="center" pr={8}>
            <HStack><FiCalendar /><Text>Annual Leave</Text></HStack>
            <HStack spacing={2}>
              {isAdmin && <Button size="xs" leftIcon={<FiSettings />} colorScheme="purple" variant="outline"
                onClick={() => { setShowAdmin(true); fetchAdmin(); fetchPeriods(); fetchDepartments(); }}>Holiday Admin</Button>}
              <Button size="xs" leftIcon={<FiPlus />} colorScheme="teal" variant={showForm ? 'solid' : 'outline'}
                onClick={() => setShowForm(!showForm)}>{showForm ? 'Cancel' : 'Request Leave'}</Button>
            </HStack>
          </Flex>
        </ModalHeader><ModalCloseButton />
        <ModalBody>
          {/* Summary cards */}
          {summary && (
            <Flex gap={3} mb={4} flexWrap="wrap">
              <Box bg="green.50" border="1px" borderColor="green.200" p={3} borderRadius="md" flex={1} minW="120px">
                <Text fontSize="9px" color="green.600" fontWeight="700" textTransform="uppercase">Remaining</Text>
                <Text fontSize="24px" fontWeight="800" color="green.700">{summary.remaining ?? '—'}</Text>
                <Text fontSize="9px" color="gray.500">days</Text>
              </Box>
              <Box bg="blue.50" border="1px" borderColor="blue.200" p={3} borderRadius="md" flex={1} minW="120px">
                <Text fontSize="9px" color="blue.600" fontWeight="700" textTransform="uppercase">Allocation</Text>
                <Text fontSize="24px" fontWeight="800" color="blue.700">{summary.allocation?.total_available ?? 25}</Text>
                <Text fontSize="9px" color="gray.500">total days</Text>
              </Box>
              <Box bg="orange.50" border="1px" borderColor="orange.200" p={3} borderRadius="md" flex={1} minW="120px">
                <Text fontSize="9px" color="orange.600" fontWeight="700" textTransform="uppercase">Taken</Text>
                <Text fontSize="24px" fontWeight="800" color="orange.700">{summary.taken ?? 0}</Text>
                <Text fontSize="9px" color="gray.500">days used</Text>
              </Box>
              <Box bg="yellow.50" border="1px" borderColor="yellow.200" p={3} borderRadius="md" flex={1} minW="120px">
                <Text fontSize="9px" color="yellow.700" fontWeight="700" textTransform="uppercase">Pending</Text>
                <Text fontSize="24px" fontWeight="800" color="yellow.700">{summary.pending ?? 0}</Text>
                <Text fontSize="9px" color="gray.500">awaiting</Text>
              </Box>
              <Box bg="red.50" border="1px" borderColor="red.200" p={3} borderRadius="md" flex={1} minW="120px">
                <Text fontSize="9px" color="red.600" fontWeight="700" textTransform="uppercase">Sickness</Text>
                <Text fontSize="24px" fontWeight="800" color="red.600">{summary.sickness ?? 0}</Text>
                <Text fontSize="9px" color="gray.500">days this year</Text>
              </Box>
              {summary.period && (
                <Box bg="gray.50" border="1px" borderColor="gray.200" p={3} borderRadius="md" flex={1} minW="140px">
                  <Text fontSize="9px" color="gray.500" fontWeight="700" textTransform="uppercase">Holiday Year</Text>
                  <Text fontSize="12px" fontWeight="700" color="gray.700">{summary.period.name}</Text>
                  <Text fontSize="9px" color="gray.400">{fmtDate(summary.period.start_date)} — {fmtDate(summary.period.end_date)}</Text>
                </Box>
              )}
            </Flex>
          )}

          {/* Request form */}
          {showForm && (
            <Box bg="teal.50" p={3} borderRadius="md" mb={4}>
              <VStack spacing={2}>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Start Date</FormLabel>
                    <Input size="sm" type="date" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>End Date</FormLabel>
                    <Input size="sm" type="date" value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} /></FormControl>
                  <FormControl flex={0.5}><FormLabel fontSize="xs" mb={0.5}>Days</FormLabel>
                    <Input size="sm" type="number" step="0.5" value={form.days_count} onChange={e => setForm(p => ({ ...p, days_count: e.target.value }))} /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Type</FormLabel>
                    <Select size="sm" value={form.request_type} onChange={e => setForm(p => ({ ...p, request_type: e.target.value }))}>
                      <option value="annual">Annual Leave</option><option value="unpaid">Unpaid</option>
                      <option value="compassionate">Compassionate</option><option value="other">Other</option>
                    </Select></FormControl>
                </HStack>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Notes</FormLabel>
                    <Input size="sm" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional" /></FormControl>
                  <Button size="sm" colorScheme="teal" onClick={handleSubmit} isLoading={saving} mt={5}>Submit Request</Button>
                </HStack>
              </VStack>
            </Box>
          )}

          {/* Filters */}
          <HStack spacing={2} mb={3}>
            <Select size="xs" value={viewRange} w="120px" onChange={e => setViewRange(e.target.value)}>
              <option value="year">Full Year</option><option value="3month">3 Months</option>
              <option value="month">This Month</option><option value="2week">2 Weeks</option>
            </Select>
            {isAdmin && <Select size="xs" w="180px" value={filterUser} onChange={e => { setFilterUser(e.target.value); if (e.target.value) fetchSummary(e.target.value); else fetchSummary(); }}>
              <option value="">My Holidays</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.display_name || u.username}</option>)}
            </Select>}
          </HStack>

          {/* Requests table */}
          {requests.length === 0 ? (
            <Text fontSize="sm" color="gray.500" textAlign="center" py={6}>No holiday requests for this period.</Text>
          ) : (
            <Table size="sm"><Thead><Tr bg="gray.50">
              <Th px={2} fontSize="9px">Dates</Th><Th px={2} fontSize="9px">Days</Th><Th px={2} fontSize="9px">Type</Th>
              <Th px={2} fontSize="9px">Status</Th><Th px={2} fontSize="9px">Notes</Th>
              {(isAdmin || filterUser) && <Th px={2} fontSize="9px">User</Th>}
              <Th px={2} fontSize="9px">Actions</Th>
            </Tr></Thead><Tbody>
              {requests.map(r => (
                <Tr key={r.id} _hover={{ bg: 'gray.50' }}>
                  <Td px={2} whiteSpace="nowrap" fontSize="11px">{fmtDate(r.start_date)} — {fmtDate(r.end_date)}</Td>
                  <Td px={2} fontSize="11px" fontWeight="600">{parseFloat(r.days_count)}</Td>
                  <Td px={2}><Badge fontSize="8px" colorScheme="blue">{TYPE_LABELS[r.request_type] || r.request_type}</Badge></Td>
                  <Td px={2}><Badge fontSize="8px" colorScheme={STATUS_COLORS[r.status]}>{r.status}</Badge></Td>
                  <Td px={2} maxW="150px" isTruncated fontSize="10px">{r.notes || '—'}</Td>
                  {(isAdmin || filterUser) && <Td px={2} fontSize="10px">{r.user_name}</Td>}
                  <Td px={2}>
                    <HStack spacing={1}>
                      {isAdmin && r.status === 'requested' && (
                        <>
                          <Button size="xs" colorScheme="green" variant="ghost" leftIcon={<FiCheck />}
                            onClick={() => handleAction(r.id, 'approve')}>Approve</Button>
                          <Button size="xs" colorScheme="red" variant="ghost" leftIcon={<FiX />}
                            onClick={() => handleAction(r.id, 'reject')}>Reject</Button>
                        </>
                      )}
                      {r.user_id === user?.id && r.status === 'requested' && (
                        <Button size="xs" variant="ghost" onClick={() => handleAction(r.id, 'withdraw')}>Withdraw</Button>
                      )}
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody></Table>
          )}
        </ModalBody>
        <ModalFooter><Button size="sm" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>

    {/* Admin modal */}
    <Modal isOpen={showAdmin} onClose={() => setShowAdmin(false)} size="6xl" scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="90vh">
        <ModalHeader fontSize="md">
          <Flex justify="space-between" align="center" pr={8}>
            <HStack><FiSettings /><Text>Holiday Administration</Text></HStack>
            <HStack>
              <Button size="xs" colorScheme="blue" variant="outline" leftIcon={<FiCalendar />}
                onClick={nationalHolidaysDisclosure.onOpen}>National Holidays</Button>
              <Button size="xs" variant="outline" onClick={() => setShowAdmin(false)}>Back to My Holidays</Button>
            </HStack>
          </Flex>
        </ModalHeader><ModalCloseButton />
        <ModalBody>
          {/* Period management */}
          <Box mb={4}>
            <Flex justify="space-between" align="center" mb={2}>
              <Text fontSize="12px" fontWeight="700" color="gray.600">Holiday Year Periods</Text>
              <Button size="xs" leftIcon={<FiPlus />} colorScheme="purple" variant="outline"
                onClick={() => setShowPeriodForm(!showPeriodForm)}>{showPeriodForm ? 'Cancel' : 'Add Period'}</Button>
            </Flex>
            {showPeriodForm && (
              <Box bg="purple.50" p={3} borderRadius="md" mb={3}>
                <HStack spacing={2} flexWrap="wrap">
                  <FormControl flex={2}><FormLabel fontSize="xs" mb={0.5}>Name</FormLabel>
                    <Input size="xs" value={periodForm.name} onChange={e => setPeriodForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. 2026/2027" /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Start</FormLabel>
                    <Input size="xs" type="date" value={periodForm.start_date} onChange={e => setPeriodForm(p => ({ ...p, start_date: e.target.value }))} /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>End</FormLabel>
                    <Input size="xs" type="date" value={periodForm.end_date} onChange={e => setPeriodForm(p => ({ ...p, end_date: e.target.value }))} /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Scope</FormLabel>
                    <Select size="xs" value={periodForm.scope} onChange={e => setPeriodForm(p => ({ ...p, scope: e.target.value }))}>
                      <option value="standard">Standard (everyone)</option><option value="department">Department</option><option value="user">User</option>
                    </Select></FormControl>
                  {periodForm.scope === 'department' && (
                    <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Department</FormLabel>
                      <Select size="xs" value={periodForm.department_id} onChange={e => setPeriodForm(p => ({ ...p, department_id: e.target.value }))}>
                        <option value="">—</option>{departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </Select></FormControl>
                  )}
                  {periodForm.scope === 'user' && (
                    <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>User</FormLabel>
                      <Select size="xs" value={periodForm.user_id} onChange={e => setPeriodForm(p => ({ ...p, user_id: e.target.value }))}>
                        <option value="">—</option>{users.map(u => <option key={u.id} value={u.id}>{u.display_name}</option>)}
                      </Select></FormControl>
                  )}
                  <Button size="xs" colorScheme="purple" mt={5} onClick={handleSavePeriod}>Save</Button>
                </HStack>
              </Box>
            )}
            {periods.length > 0 && (
              <Table size="sm" mb={4}><Thead><Tr bg="gray.50">
                <Th px={2} fontSize="9px">Name</Th><Th px={2} fontSize="9px">Dates</Th><Th px={2} fontSize="9px">Scope</Th>
              </Tr></Thead><Tbody>
                {periods.map(p => (
                  <Tr key={p.id}><Td px={2} fontSize="11px" fontWeight="600">{p.name}</Td>
                    <Td px={2} fontSize="11px">{fmtDate(p.start_date)} — {fmtDate(p.end_date)}</Td>
                    <Td px={2}><Badge fontSize="8px" colorScheme={p.scope === 'standard' ? 'blue' : p.scope === 'department' ? 'purple' : 'orange'}>{p.scope}</Badge></Td>
                  </Tr>
                ))}
              </Tbody></Table>
            )}
          </Box>

          {/* All users overview */}
          <Text fontSize="12px" fontWeight="700" color="gray.600" mb={2}>All Users — Holiday Overview</Text>
          {adminData.length === 0 ? (
            <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>Loading...</Text>
          ) : (
            <Table size="sm"><Thead><Tr bg="gray.50">
              <Th px={2} fontSize="9px">User</Th><Th px={2} fontSize="9px">Period</Th>
              <Th px={2} fontSize="9px" isNumeric>Allocation</Th><Th px={2} fontSize="9px" isNumeric>Taken</Th>
              <Th px={2} fontSize="9px" isNumeric>Pending</Th><Th px={2} fontSize="9px" isNumeric>Remaining</Th>
            </Tr></Thead><Tbody>
              {adminData.map(d => (
                <Tr key={d.user.id} _hover={{ bg: 'gray.50' }}>
                  <Td px={2} fontSize="11px" fontWeight="600">{d.user.display_name || d.user.username}</Td>
                  <Td px={2} fontSize="10px">{d.period?.name || '—'}</Td>
                  <Td px={2} isNumeric fontSize="11px">{d.allocation}</Td>
                  <Td px={2} isNumeric fontSize="11px" color="orange.600" fontWeight="600">{d.taken}</Td>
                  <Td px={2} isNumeric fontSize="11px" color="yellow.600">{d.pending}</Td>
                  <Td px={2} isNumeric fontSize="11px" fontWeight="700" color={d.remaining > 5 ? 'green.600' : d.remaining > 0 ? 'orange.600' : 'red.600'}>{d.remaining}</Td>
                </Tr>
              ))}
            </Tbody></Table>
          )}
        </ModalBody>
        <ModalFooter><Button size="sm" onClick={() => setShowAdmin(false)}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>
    <NationalHolidaysModal isOpen={nationalHolidaysDisclosure.isOpen} onClose={nationalHolidaysDisclosure.onClose} />
    </>
  );
}
